/**
 * Pure port of Containd `services/svg/DynamicSvgShapeProcessor.ts` (no logger).
 * Applies item shell colors onto SVG elements tagged with Containd auto-id suffixes.
 */

export interface SvgShapeSettings {
  isDynamicSVG: boolean;
  applyStrokeWidth?: boolean;
  applyStrokeColor?: boolean;
  applyFillColor?: boolean;
  isAspectLocked?: boolean;
  hideSquareBackground?: boolean;
  hideSquareFrame?: boolean;
  applyTextColor?: boolean;
}

export interface DynamicSvgItemColors {
  backgroundColor: string;
  borderColor: string;
  frameThickness?: number;
  textColor?: string;
  itemWidth?: number;
}

type AttributePatch = {
  suffix: string;
  attribute: 'fill' | 'stroke' | 'stroke-width';
  value: string;
};

const TAG_PATTERN = /<([a-zA-Z][\w:-]*)([^>]*?)(\/?)>/g;
const ROOT_SVG_PATTERN = /<svg\b([^>]*)>/;

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function parsePositiveNumber(value: string | undefined): number | null {
  if (value == null) return null;
  const trimmed = value.trim().replace(/px$/i, '');
  const parsed = parseFloat(trimmed);
  if (!Number.isFinite(parsed) || parsed <= 0) return null;
  return parsed;
}

export function parseSvgViewportWidth(xml: string): number | null {
  const rootMatch = xml.match(ROOT_SVG_PATTERN);
  if (!rootMatch) return null;
  const attrs = rootMatch[1]!;
  const viewBoxMatch = attrs.match(/\bviewBox=(["'])([^"']+)\1/);
  if (viewBoxMatch) {
    const parts = viewBoxMatch[2]!.trim().split(/[\s,]+/).filter(Boolean);
    if (parts.length >= 3) {
      const viewportWidth = parsePositiveNumber(parts[2]);
      if (viewportWidth != null) return viewportWidth;
    }
  }
  const widthMatch = attrs.match(/\bwidth=(["'])([^"']+)\1/);
  if (widthMatch) return parsePositiveNumber(widthMatch[2]);
  return null;
}

export function computeScaledSvgStrokeWidth(
  frameThickness: number,
  itemWidth: number,
  viewportWidth: number,
): number {
  if (
    !Number.isFinite(frameThickness) ||
    !Number.isFinite(itemWidth) ||
    !Number.isFinite(viewportWidth) ||
    itemWidth <= 0 ||
    viewportWidth <= 0
  ) {
    return frameThickness;
  }
  return (viewportWidth / itemWidth) * frameThickness;
}

function formatSvgStrokeWidth(value: number): string {
  if (!Number.isFinite(value)) return '0';
  const rounded = Math.round(value * 1000) / 1000;
  return rounded.toFixed(3).replace(/\.?0+$/, '');
}

function hasIdToken(attrs: string, suffix: string): boolean {
  const token = suffix.startsWith('-') ? suffix.slice(1) : suffix;
  const idMatch = attrs.match(/\bid=(["'])([^"']+)\1/);
  if (!idMatch) return false;
  return idMatch[2]!.split('-').includes(token);
}

function setOrAddAttribute(attrs: string, attribute: string, value: string): string {
  const attrPattern = new RegExp(`(\\s${escapeRegExp(attribute)}=(["']))[^"']*\\2`);
  if (attrPattern.test(attrs)) {
    return attrs.replace(attrPattern, `$1${value}$2`);
  }
  return `${attrs} ${attribute}="${value}"`;
}

function patchElementsBySuffix(xml: string, patches: AttributePatch[]): string {
  if (patches.length === 0) return xml;
  return xml.replace(TAG_PATTERN, (fullMatch, tagName: string, attrs: string, selfClose: string) => {
    let nextAttrs = attrs;
    let changed = false;
    for (const patch of patches) {
      if (hasIdToken(nextAttrs, patch.suffix)) {
        nextAttrs = setOrAddAttribute(nextAttrs, patch.attribute, patch.value);
        changed = true;
      }
    }
    if (!changed) return fullMatch;
    return `<${tagName}${nextAttrs}${selfClose}>`;
  });
}

function patchRootSvgAspectRatio(xml: string, isAspectLocked: boolean): string {
  const preserveAspectRatio = isAspectLocked ? 'xMidYMid meet' : 'none';
  return xml.replace(ROOT_SVG_PATTERN, (_fullMatch, attrs: string) => {
    const nextAttrs = setOrAddAttribute(attrs, 'preserveAspectRatio', preserveAspectRatio);
    return `<svg${nextAttrs}>`;
  });
}

function resolveStrokeWidth(
  colors: DynamicSvgItemColors,
  viewportWidth: number | null,
): string {
  const frameThickness = colors.frameThickness ?? 0;
  const itemWidth = colors.itemWidth;
  if (
    itemWidth != null &&
    itemWidth > 0 &&
    viewportWidth != null &&
    viewportWidth > 0
  ) {
    return formatSvgStrokeWidth(
      computeScaledSvgStrokeWidth(frameThickness, itemWidth, viewportWidth),
    );
  }
  return formatSvgStrokeWidth(frameThickness);
}

function buildPatches(
  settings: SvgShapeSettings,
  colors: DynamicSvgItemColors,
  viewportWidth: number | null,
): AttributePatch[] {
  const patches: AttributePatch[] = [];
  const strokeWidth = resolveStrokeWidth(colors, viewportWidth);
  if (settings.applyFillColor) {
    patches.push({ suffix: '-autobg', attribute: 'fill', value: colors.backgroundColor });
  }
  if (settings.applyStrokeColor) {
    patches.push({ suffix: '-autoscol', attribute: 'stroke', value: colors.borderColor });
  }
  if (settings.applyStrokeWidth) {
    patches.push({ suffix: '-autos', attribute: 'stroke-width', value: strokeWidth });
  }
  if (settings.applyTextColor && colors.textColor) {
    patches.push({ suffix: '-autotxt', attribute: 'fill', value: colors.textColor });
  }
  return patches;
}

export function parseSvgShapeSettingsJson(raw?: string | null): SvgShapeSettings | null {
  if (raw == null || raw.trim().length === 0) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<SvgShapeSettings>;
    if (parsed?.isDynamicSVG !== true) return null;
    return {
      isDynamicSVG: true,
      applyStrokeWidth: parsed.applyStrokeWidth === true,
      applyStrokeColor: parsed.applyStrokeColor === true,
      applyFillColor: parsed.applyFillColor === true,
      isAspectLocked: parsed.isAspectLocked === true,
      hideSquareBackground: parsed.hideSquareBackground === true,
      hideSquareFrame: parsed.hideSquareFrame === true,
      applyTextColor: parsed.applyTextColor === true,
    };
  } catch {
    return null;
  }
}

export function applyDynamicSvgStyles(
  xml: string,
  settings: SvgShapeSettings | null | undefined,
  colors: DynamicSvgItemColors,
): string {
  if (!settings?.isDynamicSVG || !xml.includes('<')) return xml;
  try {
    const viewportWidth = parseSvgViewportWidth(xml);
    const patches = buildPatches(settings, colors, viewportWidth);
    let result = patchElementsBySuffix(xml, patches);
    result = patchRootSvgAspectRatio(result, settings.isAspectLocked === true);
    return result;
  } catch {
    return xml;
  }
}
